import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";

type ButtonLikeProps = {
  variant?: string;
  size?: string;
  children?: ReactNode;
} & Record<string, unknown>;

type Slot = "primary" | "secondary" | "tertiary" | "destructive";

export interface ActionBarProps {
  /** The single primary action for this surface. Applied variant: `default` (filled). */
  primary?: ReactElement<ButtonLikeProps>;
  /** Supporting actions. Applied variant: `outline`. */
  secondary?: ReactElement<ButtonLikeProps>[];
  /** Quiet/auxiliary actions. Applied variant: `ghost`. */
  tertiary?: ReactElement<ButtonLikeProps>[];
  /** A destructive action. Applied variant: `destructive`. Replaces primary when both are passed. */
  destructive?: ReactElement<ButtonLikeProps>;
  /** Size applied to every slot child that does not set its own. Defaults to `sm` (page toolbars). */
  size?: "sm" | "default" | "lg";
  /** Horizontal alignment. Defaults to `end`. */
  align?: "end" | "start" | "between";
  className?: string;
}

const SLOT_VARIANT: Record<Slot, string> = {
  primary: "default",
  secondary: "outline",
  tertiary: "ghost",
  destructive: "destructive",
};

const ALIGN_CLASS = {
  end: "justify-end",
  start: "justify-start",
  between: "justify-between",
} as const;

let hasWarnedVariantOverride = false;
let hasWarnedBothPrimaryAndDestructive = false;

/**
 * Opinionated layout primitive that enforces the dashboard's four-slot
 * button hierarchy (primary / secondary / tertiary / destructive).
 *
 * Callers pass bare `<Button>Label</Button>` children — ActionBar clones
 * each element and sets the matching `variant` and `size`. An explicit
 * `variant=` on a child is honored (escape hatch) but warns once in dev.
 *
 * Ordering is left → right: tertiary, secondary, primary (or destructive).
 * Matches the established `Cancel → Save` dialog-footer convention.
 */
export function ActionBar({
  primary,
  secondary,
  tertiary,
  destructive,
  size = "sm",
  align = "end",
  className,
}: ActionBarProps) {
  if (import.meta.env.DEV && primary && destructive) {
    warnBothPrimaryAndDestructive();
  }

  const finalPrimarySlot = destructive ?? primary ?? null;

  const rendered: ReactElement[] = [];

  tertiary?.forEach((el, idx) => {
    const cloned = applySlot(el, "tertiary", size, `tertiary-${idx}`);
    if (cloned) rendered.push(cloned);
  });

  secondary?.forEach((el, idx) => {
    const cloned = applySlot(el, "secondary", size, `secondary-${idx}`);
    if (cloned) rendered.push(cloned);
  });

  if (finalPrimarySlot) {
    const slot: Slot = destructive ? "destructive" : "primary";
    const cloned = applySlot(finalPrimarySlot, slot, size, slot);
    if (cloned) rendered.push(cloned);
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-3",
        ALIGN_CLASS[align],
        className,
      )}
      data-slot="action-bar"
    >
      {rendered}
    </div>
  );
}

function applySlot(
  element: ReactElement<ButtonLikeProps>,
  slot: Slot,
  defaultSize: ActionBarProps["size"],
  key: string,
): ReactElement | null {
  if (!isValidElement(element)) return null;

  const existingProps = element.props as ButtonLikeProps;
  const nextProps: ButtonLikeProps = { key };

  if (existingProps.variant === undefined) {
    nextProps.variant = SLOT_VARIANT[slot];
  } else if (import.meta.env.DEV) {
    warnVariantOverride(slot, existingProps.variant);
  }

  if (existingProps.size === undefined && defaultSize !== undefined) {
    nextProps.size = defaultSize;
  }

  return cloneElement(element, nextProps);
}

function warnVariantOverride(slot: Slot, actual: string): void {
  if (hasWarnedVariantOverride) return;
  hasWarnedVariantOverride = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[ActionBar] slot "${slot}" received a child with explicit variant="${actual}". ` +
      `The slot's canonical variant is "${SLOT_VARIANT[slot]}". ` +
      `Pass bare <Button>Label</Button> children and let ActionBar set the variant.`,
  );
}

function warnBothPrimaryAndDestructive(): void {
  if (hasWarnedBothPrimaryAndDestructive) return;
  hasWarnedBothPrimaryAndDestructive = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[ActionBar] received both primary and destructive props. ` +
      `A surface should have one or the other; destructive replaces primary. ` +
      `Rendering only the destructive action.`,
  );
}
